import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './RegistrationForm.css';
function getOrCreateSessionToken() {
    if (typeof window === 'undefined') return null;
    let token = window.localStorage.getItem('study_session_token');
    if (!token) {
        // crypto.randomUUID is supported in modern browsers; if not, you could fallback
        token = crypto.randomUUID();
        window.localStorage.setItem('study_session_token', token);
    }
    return token;
}
function EvaluatorRegistration() {
    const navigate = useNavigate();
    const { search } = useLocation();

    const [form, setForm] = useState({
        email: '',
        pronouns: '',
        creative_writing_freq: '',
        poetry_experience: '',
        reading_poetry_freq: '',
        music_focus: '',
        ai_familiarity: '',
        ai_tools_used: [],
        ai_reliance: '',
    });

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const updateField = (field, value) =>
        setForm(prev => ({ ...prev, [field]: value }));

    const toggleTool = (tool) => {
        setForm(prev => {
            const exists = prev.ai_tools_used.includes(tool);
            return {
                ...prev,
                ai_tools_used: exists
                    ? prev.ai_tools_used.filter(t => t !== tool)
                    : [...prev.ai_tools_used, tool],
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!form.email.trim()) {
            setError('Email address is required.');
            return;
        }
        if (!form.pronouns) {
            setError('Please select your preferred pronouns.');
            return;
        }

        setSubmitting(true);
        try {
            const sessionToken = getOrCreateSessionToken();
            const res = await fetch('/api/register/evaluator', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...form,
                    session_token: sessionToken,
                }),
            });

            const data = await res.json();
            if (!res.ok || !data.success) {
                throw new Error(data.error || 'Registration failed');
            }

            const { pid } = data;

            localStorage.setItem('pid', pid);

            const sp = new URLSearchParams(search || '');
            sp.set('interface', 'annotate');
            sp.set('pid', pid);

            navigate({
                pathname: '/consent',
                search: `?${sp.toString()}`,
            });

        } catch (err) {
            console.error(err);
            setError(err.message || 'Server error during registration.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleBackHome = () => navigate('/');

    return (
        <div className="registration-page">
            <div className="registration-card">
                <button className="reg-back-link" onClick={handleBackHome}>
                    ← Back to Home
                </button>

                <div className="registration-header">
                    <div>
                        <h1 className="registration-title">
                            Session Study Registration – Evaluators
                        </h1>
                        <p className="registration-subtitle">
                            Register to participate as an evaluator/annotator for poems in the study.
                        </p>
                    </div>
                    <div className="registration-badge">Annotator</div>
                </div>
                <div className="reg-note">
                    <strong>Note:</strong> If you have already registered but did not complete the task,
                    you can overwrite your registration details by entering the same email address you used previously.
                </div>

                <form className="registration-form" onSubmit={handleSubmit}>

                    {/* Email */}
                    <div className="reg-field">
                        <div className="reg-label-row">
                            <label className="reg-label">Email Address</label>
                            <span className="reg-required">*</span>
                        </div>
                        <input
                            type="email"
                            className="reg-input"
                            value={form.email}
                            onChange={e => updateField('email', e.target.value)}
                            placeholder="Please enter an email"
                            required
                        />
                    </div>

                    {/* Pronouns */}
                    <div className="reg-field">
                        <div className="reg-label-row">
                            <span className="reg-label">Preferred Pronouns</span>
                            <span className="reg-required">*</span>
                        </div>
                        <div className="reg-radio-group">
                            {['She/Her', 'He/Him', 'They/Them', 'Prefer not to say', 'Other'].map(opt => (
                                <label key={opt} className="reg-radio-option">
                                    <input
                                        type="radio"
                                        name="pronouns"
                                        value={opt}
                                        checked={form.pronouns === opt}
                                        onChange={e => updateField('pronouns', e.target.value)}
                                    />
                                    <span>{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Creative writing frequency */}
                    <div className="reg-field">
            <span className="reg-label">
              How often do you engage in creative writing (e.g., poetry, fiction, stories)?
            </span>
                        <div className="reg-radio-group">
                            {[
                                'Daily',
                                'A few times a week',
                                'A few times a month',
                                'Never',
                            ].map(opt => (
                                <label key={opt} className="reg-radio-option">
                                    <input
                                        type="radio"
                                        name="creative_writing_freq"
                                        value={opt}
                                        checked={form.creative_writing_freq === opt}
                                        onChange={e => updateField('creative_writing_freq', e.target.value)}
                                    />
                                    <span>{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Poetry experience */}
                    <div className="reg-field">
            <span className="reg-label">
              Which of the following best describes your experience with writing poetry specifically?
            </span>
                        <div className="reg-radio-group">
                            {[
                                'Novice: I have little to no experience writing poetry.',
                                'Hobbyist/Intermediate: I write for enjoyment, but haven\'t been published or trained.',
                                'Expert: I had formal training in poetry (e.g., coursework) / my poetry has been published.',
                            ].map(opt => (
                                <label key={opt} className="reg-radio-option">
                                    <input
                                        type="radio"
                                        name="poetry_experience"
                                        value={opt}
                                        checked={form.poetry_experience === opt}
                                        onChange={e => updateField('poetry_experience', e.target.value)}
                                    />
                                    <span>{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Reading poetry frequency */}
                    <div className="reg-field">
                        <span className="reg-label">How often do you read poetry?</span>
                        <div className="reg-radio-group">
                            {[
                                'Very often (weekly)',
                                'Sometimes (monthly)',
                                'Rarely (a few times a year)',
                                'Never',
                            ].map(opt => (
                                <label key={opt} className="reg-radio-option">
                                    <input
                                        type="radio"
                                        name="reading_poetry_freq"
                                        value={opt}
                                        checked={form.reading_poetry_freq === opt}
                                        onChange={e => updateField('reading_poetry_freq', e.target.value)}
                                    />
                                    <span>{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Music vs lyrics */}
                    <div className="reg-field">
            <span className="reg-label">
              When listening to music with lyrics, what do you tend to focus on more?
            </span>
                        <div className="reg-radio-group">
                            {[
                                'Mostly the music, melody, and rhythm.',
                                'Mostly the lyrics and the story they tell.',
                                'An equal balance of both',
                            ].map(opt => (
                                <label key={opt} className="reg-radio-option">
                                    <input
                                        type="radio"
                                        name="music_focus"
                                        value={opt}
                                        checked={form.music_focus === opt}
                                        onChange={e => updateField('music_focus', e.target.value)}
                                    />
                                    <span>{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* AI familiarity */}
                    <div className="reg-field">
            <span className="reg-label">
              How familiar are you with AI writing tools like ChatGPT, Gemini, etc.?
            </span>
                        <div className="reg-radio-group">
                            {[
                                'Very familiar: I use them regularly.',
                                'Somewhat familiar: I have used them a few times.',
                                'Not familiar at all: I have little to no experience with them.',
                            ].map(opt => (
                                <label key={opt} className="reg-radio-option">
                                    <input
                                        type="radio"
                                        name="ai_familiarity"
                                        value={opt}
                                        checked={form.ai_familiarity === opt}
                                        onChange={e => updateField('ai_familiarity', e.target.value)}
                                    />
                                    <span>{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* AI tools used */}
                    <div className="reg-field">
            <span className="reg-label">
              Which of the following AI writing tools have you used? (Select all that apply)
            </span>
                        <div className="reg-checkbox-group">
                            {['ChatGPT', 'Gemini', 'DeepSeek', 'Microsoft Copilot', 'Other'].map(opt => (
                                <label key={opt} className="reg-checkbox-option">
                                    <input
                                        type="checkbox"
                                        value={opt}
                                        checked={form.ai_tools_used.includes(opt)}
                                        onChange={() => toggleTool(opt)}
                                    />
                                    <span>{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* AI reliance */}
                    <div className="reg-field">
            <span className="reg-label">
              When you use AI writing tools, how often do you rely on their suggestions?
            </span>
                        <div className="reg-radio-group">
                            {[
                                'Frequently: I often accept the AI\'s suggestions as they are.',
                                'Sometimes: I use the suggestions as a starting point but edit them heavily.',
                                'Rarely: I mostly use the AI for ideas, but write the text myself.',
                                'I don\'t have enough experience with AI writing tools to say.',
                            ].map(opt => (
                                <label key={opt} className="reg-radio-option">
                                    <input
                                        type="radio"
                                        name="ai_reliance"
                                        value={opt}
                                        checked={form.ai_reliance === opt}
                                        onChange={e => updateField('ai_reliance', e.target.value)}
                                    />
                                    <span>{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {error && <div className="reg-error">{error}</div>}

                    <div className="reg-footer">
                        <button
                            type="submit"
                            className="reg-button-primary"
                            disabled={submitting}
                        >
                            {submitting ? 'Registering…' : 'Register & Start Annotating'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default EvaluatorRegistration;
